<template>
    <!-- Freshchat injects its widget into the document via external script -->
    <span hidden aria-hidden="true" />
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import * as freshchat from '@/components/chatbot/schedulePendingChatOpen.js';
import { initialState } from '@/Helpers.js';

const { mapState } = createNamespacedHelpers('Auth');

export const FRESHCHAT_LAUNCHER_SELECTORS = '[data-testid="launcher"], .fc-launcher, [class*="launcher"]';

export const HELP_SUPPORT_BODY_CLASS = 'help-support-page';

/**
 * Freshworks' fw-cdn bootstrap looks up `#freshchat-js-sdk` to decide whether
 * to inject widget.js. That id must stay reserved for widget.js. Putting it on
 * our loader makes `fcWidget.init` run before the SDK exists, so Chat now is a
 * no-op.
 */
export const FRESHCHAT_SCRIPT_ID = 'freshchat-js-sdk';

export const FRESHCHAT_LOADER_SCRIPT_ID = 'edverum-freshchat-loader';

export const CHATBOT_STATUS_EVENT = 'edverum-chatbot-status';

export const CHATBOT_OPEN_UNCONFIRMED_EVENT = 'edverum-chatbot-open-unconfirmed';

export function getChatbotStatus() {
    const stored = window.edverumChatbot?.status;
    if (stored === 'ready' || stored === 'unavailable' || stored === 'loading') {
        return stored;
    }

    return window.edverumChatbot ? 'ready' : 'loading';
}

export default {
    name: 'ChatbotInitializer',

    data: () => ({
        isInitialized: false,
        scriptLoaded: false,
        pendingOpen: false,
        pendingOpenTimer: null,
        pendingOpenDeadline: null,
        expectingOpen: false,
        isWidgetReady: false,
        widgetEventsBound: false,
        widgetReadyPromise: null,
        resolveWidgetReady: null,
        rejectWidgetReady: null,
    }),

    computed: {
        ...mapState(['user']),
        chatbotConfig() {
            return {
                enabled: initialState('ChatbotConfig.enabled', true),
                widgetId: initialState('ChatbotConfig.widgetId', ''),
                scriptUrl: initialState('ChatbotConfig.scriptUrl', ''),
                roleMapping: initialState('ChatbotConfig.roleMapping', {}),
                primaryGroupCategories: initialState('ChatbotConfig.primaryGroupCategories', {}),
                widgetConfig: initialState('ChatbotConfig.widgetConfig', {}),
            };
        },
        shouldHideLauncher() {
            return this.chatbotConfig.widgetConfig.hide_launcher ?? true;
        },
        userRole() {
            if (!this.user) return null;
            const userRoles = this.user.roles || [];
            const mappedRole = userRoles
                .map(role => this.chatbotConfig.roleMapping[role.name])
                .find(mapping => mapping);
            return mappedRole || 'edverum_student';
        },
        primaryGroup() {
            if (!this.user || !this.user.userGroups) return null;
            const institutionName = initialState('InstitutionSettings.name', '').toLowerCase();
            const categoryName =
                this.chatbotConfig.primaryGroupCategories[institutionName] ||
                this.chatbotConfig.primaryGroupCategories.default ||
                'Cohort';
            const primaryGroup = this.user.userGroups.find(group => group.group?.category?.name === categoryName);
            return primaryGroup?.group?.name || null;
        },
        shouldInitialize() {
            return (
                this.user &&
                this.chatbotConfig.enabled &&
                !this.isInitialized &&
                !this.$store.getters['User/hasActiveAttempt'] &&
                this.isHelpPage
            );
        },
        isHelpPage() {
            return this.$route.path === '/help' || this.$route.path.startsWith('/help/');
        },
    },

    watch: {
        user: {
            handler() {
                this.notifyIfChatbotCannotStart();
                if (this.shouldInitialize) this.initializeChatbot();
            },
            immediate: true,
        },
        '$store.getters.User/hasActiveAttempt': {
            handler(hasActiveAttempt) {
                if (hasActiveAttempt) {
                    freshchat.cancelQueuedChatOpen(this);
                    return;
                }

                if (this.shouldInitialize) this.initializeChatbot();
            },
            immediate: true,
        },
        $route: {
            handler() {
                if (this.isHelpPage) {
                    this.setHelpPageActive(true);
                    this.notifyIfChatbotCannotStart();

                    if (this.shouldInitialize) {
                        this.initializeChatbot();
                    } else if (this.isInitialized) {
                        this.hideLauncher();
                    }
                } else if (this.isInitialized) {
                    this.setHelpPageActive(false);
                    this.hideWidgetOffPage();
                } else {
                    this.setHelpPageActive(false);
                }
            },
            immediate: true,
        },
    },

    beforeUnmount() {
        this.destroyChatbot();
    },

    methods: {
        buildMessengerConfig() {
            const headerProperty = {
                appName: this.chatbotConfig.widgetConfig.app_name ?? 'Edverum Support',
            };

            if (this.shouldHideLauncher) {
                headerProperty.hideChatButton = true;
            }

            return {
                config: {
                    eagerLoad: this.chatbotConfig.widgetConfig.eager_load ?? false,
                    fullscreen: this.chatbotConfig.widgetConfig.fullscreen ?? false,
                    headerProperty,
                },
                tags: [this.userRole],
                externalId: this.user.id.toString(),
            };
        },

        setHelpPageActive(active) {
            document.body.classList.toggle(HELP_SUPPORT_BODY_CLASS, active);
        },

        notifyIfChatbotCannotStart() {
            if (!this.isHelpPage || this.isInitialized || this.$store.getters['User/hasActiveAttempt']) {
                return;
            }

            if (
                this.user &&
                (!this.chatbotConfig.enabled || !this.chatbotConfig.widgetId || !this.chatbotConfig.scriptUrl)
            ) {
                this.publishChatbotStatus('unavailable');
            }
        },

        publishChatbotStatus(status, extra = {}) {
            const current =
                window.edverumChatbot && typeof window.edverumChatbot === 'object' ? window.edverumChatbot : {};
            current.status = status;
            window.edverumChatbot = current;
            window.dispatchEvent(new CustomEvent(CHATBOT_STATUS_EVENT, { detail: { status, ...extra } }));
        },

        bindWidgetEvents() {
            if (!window.fcWidget || typeof window.fcWidget.on !== 'function' || this.widgetEventsBound) return;

            this.widgetEventsBound = true;
            freshchat.bindFreshchatOpenEvents(window.fcWidget, this);
        },

        exposeChatbotApi() {
            window.edverumChatbot = {
                open: () => this.openChatbot(),
                close: () => window.fcWidget && window.fcWidget.close(),
                isOpen: () => window.fcWidget && window.fcWidget.isOpen(),
                show: () => this.showChatbot(),
                hide: () => this.hideChatbot(),
                toggle: () => this.toggleChatbot(),
            };
            this.publishChatbotStatus(this.isWidgetReady ? 'ready' : 'loading');
        },

        async initializeChatbot() {
            if (!this.shouldInitialize) return;
            if (!this.chatbotConfig.widgetId || !this.chatbotConfig.scriptUrl) {
                console.warn('Chatbot: Widget ID or Script URL missing.');
                this.publishChatbotStatus('unavailable');
                return;
            }

            window.fcWidgetMessengerConfig = this.buildMessengerConfig();
            window.fcSettings = {
                onInit: () => {
                    if (!window.fcWidget) {
                        this.failWidgetReady(new Error('Chatbot widget is unavailable.'));
                        return;
                    }
                    try {
                        freshchat.configureFreshchatUser(window.fcWidget, this);
                        this.bindWidgetEvents();
                        this.hideLauncher();
                    } catch (error) {
                        console.error('Chatbot: Failed to configure widget user.', error);
                    }
                    this.markWidgetReady();
                },
            };
            this.setHelpPageActive(true);
            this.exposeChatbotApi();
            try {
                await this.loadFreshchatScript();
            } catch {
                this.removeFreshchatScript();
                this.resetWidgetReadyState();
                this.pendingOpen = false;
                if (!this.$store.getters['User/hasActiveAttempt']) this.publishChatbotStatus('unavailable');
                return;
            }
            this.isInitialized = true;
            this.hideLauncher();
        },

        openChatbot() {
            if (!freshchat.chatOpenAllowed(this)) {
                freshchat.clearQueuedChatOpen(this);
                return;
            }

            if (freshchat.restoreAlreadyOpenWidget(window.fcWidget, this.isWidgetReady, this)) return;

            this.expectingOpen = true;
            this.pendingOpen = true;

            if (this.isWidgetReady && window.fcWidget) {
                this.resumePendingOpen(true);
                return;
            }

            freshchat.rememberPendingChatOpen(this);
            this.ensureWidgetReadyPromise()
                .then(() => this.resumePendingOpen())
                .catch(() => (this.pendingOpen = false));
        },

        resumePendingOpen(openImmediately = false) {
            freshchat.resumeQueuedChatOpen(this, CHATBOT_OPEN_UNCONFIRMED_EVENT, openImmediately);
        },

        clearPendingOpenTimer() {
            this.pendingOpenTimer?.();
            this.pendingOpenTimer = null;

            if (this.pendingOpenDeadline) {
                clearTimeout(this.pendingOpenDeadline);
                this.pendingOpenDeadline = null;
            }
        },

        prepareWidgetForOpen() {
            document.body.classList.add('chatbot-visible');

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame) {
                fcFrame.style.removeProperty('display');
                fcFrame.style.removeProperty('visibility');
                fcFrame.style.removeProperty('opacity');
            }
        },

        hideLauncher() {
            if (!this.shouldHideLauncher) return;

            document.querySelectorAll(FRESHCHAT_LAUNCHER_SELECTORS).forEach(element => {
                element.style.display = 'none';
                element.style.visibility = 'hidden';
                element.style.opacity = '0';
            });
        },

        hideWidgetOffPage() {
            document.body.classList.remove('chatbot-visible');

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame) {
                fcFrame.style.display = 'none';
                fcFrame.style.visibility = 'hidden';
                fcFrame.style.opacity = '0';
            }

            this.hideLauncher();
        },

        showChatbot() {
            if (!window.fcWidget) return;

            this.prepareWidgetForOpen();

            if (!this.shouldHideLauncher) {
                document.querySelectorAll(FRESHCHAT_LAUNCHER_SELECTORS).forEach(element => {
                    element.style.display = 'block';
                    element.style.visibility = 'visible';
                    element.style.opacity = '1';
                });
            }
        },

        hideChatbot() {
            if (!window.fcWidget) return;

            this.hideWidgetOffPage();
        },

        toggleChatbot() {
            if (!window.fcWidget) return;

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame && fcFrame.style.display !== 'none') {
                this.hideChatbot();
            } else {
                this.openChatbot();
            }
        },

        loadFreshchatScript() {
            if (this.isWidgetReady && window.fcWidget) {
                return Promise.resolve();
            }

            if (!this.widgetReadyPromise) {
                this.removeFreshchatScript();
            }

            const widgetReady = this.ensureWidgetReadyPromise();

            if (!document.getElementById(FRESHCHAT_LOADER_SCRIPT_ID)) {
                this.injectFreshchatScript();
            }

            return widgetReady;
        },

        injectFreshchatScript() {
            const script = document.createElement('script');
            script.id = FRESHCHAT_LOADER_SCRIPT_ID;
            script.src = this.chatbotConfig.scriptUrl;
            script.async = true;
            // Freshworks requires chat="true" so the messenger iframe loads on
            // the Freshchat host. Without it, postMessage targets the Freshchat
            // origin while the frame is still on the app origin (e.g. staging).
            script.setAttribute('chat', 'true');
            // Do not set widgetId. The fw-cdn bootstrap copies script[widgetId]
            // into fcWidget.init({ widgetUuid }), and already embeds the account
            // token. A mismatched UUID (the config id) prevents the widget from
            // initializing, so Chat now does nothing.
            script.onload = () => {
                this.scriptLoaded = true;
            };
            script.onerror = () => {
                console.error('Chatbot: Failed to load Freshchat script.');
                this.removeFreshchatScript();
                this.failWidgetReady(new Error('Chatbot script failed to load.'));
            };
            document.head.appendChild(script);
        },

        ensureWidgetReadyPromise() {
            if (!this.widgetReadyPromise) {
                this.widgetReadyPromise = new Promise((resolve, reject) => {
                    this.resolveWidgetReady = resolve;
                    this.rejectWidgetReady = reject;
                });
            }

            return this.widgetReadyPromise;
        },

        markWidgetReady() {
            if (!window.fcWidget) {
                return;
            }

            this.isWidgetReady = true;
            this.publishChatbotStatus('ready');
            this.resolveWidgetReady?.();
        },

        failWidgetReady(error) {
            this.pendingOpen = false;
            this.expectingOpen = false;
            this.clearPendingOpenTimer();
            const reject = this.rejectWidgetReady;
            this.resetWidgetReadyState();
            reject?.(error);
        },

        resetWidgetReadyState() {
            this.widgetReadyPromise = null;
            this.resolveWidgetReady = null;
            this.rejectWidgetReady = null;
            this.scriptLoaded = false;
            this.isWidgetReady = false;
        },

        removeFreshchatScript() {
            document.getElementById(FRESHCHAT_LOADER_SCRIPT_ID)?.remove();
            document.getElementById(FRESHCHAT_SCRIPT_ID)?.remove();
            this.scriptLoaded = false;
        },

        destroyChatbot() {
            if (window.fcWidget && typeof window.fcWidget.destroy === 'function') {
                window.fcWidget.destroy();
            }

            this.setHelpPageActive(false);
            document.body.classList.remove('chatbot-visible');

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame) {
                fcFrame.remove();
            }

            this.failWidgetReady(new Error('Chatbot destroyed.'));
            this.removeFreshchatScript();
            delete window.edverumChatbot;
            delete window.fcWidget;
            this.isInitialized = false;
            this.widgetEventsBound = false;
        },
    },
};
</script>
